import Quill from 'quill';
import {HttpService} from '../../../app/services/http.service';
import {catchError, finalize, first, take, tap} from 'rxjs/operators';
import {Observable, ObservableInput, of, switchMap} from 'rxjs';
import {showAiActionsMarkTextPopup} from './ai-actions-mark-text-popup';
import {showAiActionsHelpMeWrite} from './ai-actions-help-me-write-popup';
import {AppSettingsService} from '../../../app/services/app-settings.service';
import {AnalyticsServiceNameModel, TelemetryService} from 'telemetry-library';
import {showAiActionsUnavailablePopup} from './ai-actions-unavailable-popup';
import {UserService} from '../../../app/services/shared/user.service';
import {showAiActionsTooltip} from './ai-actions-tooltip';
import {StorageService} from '../../../app/services/storage.service';
import FeatureFlags from '../../../app/account/my-account/model/FeatureFlags.enum';
import {getOverflowHiddenAncestor} from './ai-actions-utils';

const Module = Quill.import('core/module');
const QUILL_MENU_EDGE_SPACE = 50;

export function isCovercyAiActionsFeatureEnabled(): boolean {
  // tslint:disable-next-line:no-bitwise
  return (AiActionsButtonModule.storageService?.getCredentialsData()?.featureFlags & FeatureFlags.AiService) !== 0;
}

export default class AiActionsButtonModule extends Module {
  public static http?: HttpService;
  public static appSettings?: AppSettingsService;
  public static telemetryService?: TelemetryService;
  public static userService?: UserService;
  public static storageService?: StorageService;

  baseEndpoint$: Observable<string> = AiActionsButtonModule.appSettings.localEnv ? of('http://localhost:7071/api/ai') : of('ai');

  constructor(quill, options) {
    super(quill, options);
    const toolbar = quill.getModule('toolbar');

    if (toolbar) {
      toolbar.addHandler('ai-actions', this.handleAiAction.bind(this));
    }

    requestAnimationFrame(async () => {
      const quillRootElement = this.getQuillRootElement(this.quill);
      const aiActionsButton = quillRootElement.querySelector('.ql-ai-actions');

      if (isCovercyAiActionsFeatureEnabled()) {
        if (aiActionsButton) {
          if (window.localStorage.getItem('ai-actions-tooltip-shown') !== 'true' && !(quillRootElement.closest('mat-dialog-content') || quillRootElement.closest('[mat-dialog-content]'))) {
            showAiActionsTooltip(aiActionsButton).then(() => {
              window.localStorage.setItem('ai-actions-tooltip-shown', 'true');
            });
          }
        }
      }

      this.addAiActionsMenuOpenDirectionHandler(quillRootElement);
    });
  }

  addAiActionsMenuOpenDirectionHandler(quillRootElement: HTMLElement) {
    quillRootElement.querySelector('.ql-ai-actions.ql-picker')?.addEventListener('mousedown', () => {
      const menuHeight = quillRootElement.querySelector('.ql-ai-actions.ql-picker .ql-picker-options')?.clientHeight;
      let ancestor = quillRootElement.closest('mat-dialog-content') || quillRootElement.closest('[mat-dialog-content]');

      if (!ancestor) {
        ancestor = getOverflowHiddenAncestor(quillRootElement);
      }

      const bottomEdge = Math.min(ancestor.getBoundingClientRect().bottom, window.innerHeight);
      const quillTop = quillRootElement.getBoundingClientRect().top;

      if (bottomEdge - quillTop < menuHeight + QUILL_MENU_EDGE_SPACE) {
        quillRootElement.classList.add('ai-actions-menu-open-upwards');
      } else {
        quillRootElement.classList.remove('ai-actions-menu-open-upwards');
      }
    });
  }

  aiActionCodeToCommand(aiActionCode: string): string {
    switch (aiActionCode) {
      case 'improve':
        return 'Improve the phrasing of the text';
      case 'formalize':
        return 'Formalize the text';
      case 'make-longer':
        return 'Make the text longer';
      case 'make-shorter':
        return 'Make the text shorter';
    }
    throw new Error(`Unknown AI action code: ${aiActionCode}`);
  }

  getQuillRootElement(quill: Quill): HTMLElement {
    let ret: HTMLElement = quill.root;

    while (ret.tagName.toLowerCase() !== 'quill-editor') {
      ret = ret.parentElement;

      if (!ret) {
        throw new Error('Quill root element not found');
      }
    }

    return ret;
  }

  sendAnalyticsEvent(featureEnabled: boolean, aiActionType: string, aiActionCommand?: string) {
    AiActionsButtonModule.userService?.accountDetails$
      .pipe(take(1))
      .subscribe((clientDetails) => {
        AiActionsButtonModule.telemetryService?.create({
          eventID: '9001',
          eventTitle: 'AI-Smart-Text',
          organizationID: clientDetails.organizationDetails.id,
          pageTitle: document.title,
          featureEnabled,
          aiActionType,
          aiActionCommand,
          // tslint:disable-next-line:no-bitwise
        }, AnalyticsServiceNameModel.Mixpanel | AnalyticsServiceNameModel.Insights);
      });
  }

  async handleAiAction(value: any) {
    const quill = this.quill;

    const featureEnabled = isCovercyAiActionsFeatureEnabled();

    if (!featureEnabled) {
      this.sendAnalyticsEvent(false, value, undefined);
      await showAiActionsUnavailablePopup('Covercy AI is currently available by invitation only. We’ll let you know as soon as it’s your turn!');
      return;
    }

    const selection = quill.getSelection();
    let aiCommand: string | undefined;
    let aiActionCode: string | undefined;
    if (value === 'help-me-write') {
      aiCommand = await showAiActionsHelpMeWrite();
      aiActionCode = 'Help Me Write';
    } else {
      aiCommand = this.aiActionCodeToCommand(value);
      aiActionCode = aiCommand;
    }

    if (!aiCommand) {
      return;
    }

    this.sendAnalyticsEvent(true, value, aiActionCode);

    const quillRootElement = this.getQuillRootElement(quill);

    if (!selection || selection.length === 0) {
      const dialogResult = await showAiActionsMarkTextPopup();
      switch (dialogResult) {
        case 'cancel':
          return;
        case 'let-me-mark':
          return;
        case 'modify-all':
          selection.index = 0;
          selection.length = quill.getLength();
          break;
      }
    } else {
      // if the selection contains a newline character, the new paragraph will override it,
      // and this looks weird in terms of UX
      if (quill.getText(selection.index, selection.length).endsWith('\n')) {
        selection.length -= 1;
      }
    }

    function cleanExit() {
      quill.enable();
      quillRootElement.classList.remove('ai-action-in-progress');
    }

    quill.disable();
    quillRootElement.classList.add('ai-action-in-progress');

    const selectedText = quill.getText(selection.index, selection.length);
    const entireText = quill.getText();

    this.baseEndpoint$.pipe(
      switchMap((endpoint) =>
        AiActionsButtonModule.http.post(`${endpoint}/process-paragraph`, {
          paragraphText: selectedText,
          entireText,
          command: aiCommand,
        })),
      first(),
      catchError((error, caught): ObservableInput<any> => {
        showAiActionsUnavailablePopup('Covercy AI is currently unavailable. Please try again later.')
          .then(() => {
            cleanExit();
            console.log('error', error);
          });
        return of(null)
      }),
      tap((response) => {
        if(response) {
          quill.deleteText(selection.index, selection.length);
          quill.enable(); // re-enable, disabled quill is not emitting user like events
          quill.insertText(selection.index, response?.paragraphText, 'user');
          quill.setSelection(selection.index + response?.paragraphText.length, 0);
        }
      }),
      finalize(() => {
        cleanExit();
      })
    ).subscribe();
  }
}

Quill.register('modules/aiActionsButton', AiActionsButtonModule);
